import React, { useState, useEffect, useRef } from 'react'

export interface IDottedDate {
  day: string
  month: string
  year: string
}

interface DottedDateInputProps {
  value: IDottedDate
  onChange: (value: IDottedDate) => void
  className?: string
}

type FocusedPart = 'day' | 'month' | 'year'

const DottedDateInput: React.FC<DottedDateInputProps> = ({ value, onChange, className }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const dayRef = useRef<HTMLSpanElement>(null)
  const monthRef = useRef<HTMLSpanElement>(null)
  const yearRef = useRef<HTMLSpanElement>(null)
  const focusTimeoutRef = useRef<number | null>(null)

  const [focusedPart, setFocusedPart] = useState<FocusedPart>('day')
  const [internalValue, setInternalValue] = useState<IDottedDate>(value)

  const pad = (s: string, len: number) => {
    return s.padStart(len, '0')
  }

  const getDisplayValue = (): string => {
    const day = internalValue.day ? pad(internalValue.day, 2) : 'dd'
    const month = internalValue.month ? pad(internalValue.month, 2) : 'mm'
    const year = internalValue.year ? pad(internalValue.year, 4) : 'yyyy'
    return `${day}.${month}.${year}`
  }

  const displayValue = getDisplayValue()

  // Update cursor position
  useEffect(() => {
    if (!inputRef.current) {
      return
    }

    const positions = { day: [0, 2], month: [3, 5], year: [6, 10] }
    const [start, end] = positions[focusedPart]
    inputRef.current.setSelectionRange(start, end)
  }, [focusedPart, displayValue])

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault()
    const x = e.clientX

    if (dayRef.current && monthRef.current && yearRef.current) {
      const dayRect = dayRef.current.getBoundingClientRect()
      const monthRect = monthRef.current.getBoundingClientRect()
      const yearRect = yearRef.current.getBoundingClientRect()

      if (x >= dayRect.left && x <= dayRect.right) {
        setFocusedPart('day')
      } else if (x >= monthRect.left && x <= monthRect.right) {
        setFocusedPart('month')
      } else if (x >= yearRect.left && x <= yearRect.right) {
        setFocusedPart('year')
      }
    }

    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleBlur = () => {
    const paddedDay = internalValue.day ? pad(internalValue.day, 2) : ''
    const paddedMonth = internalValue.month ? pad(internalValue.month, 2) : ''
    const paddedYear = internalValue.year ? pad(internalValue.year, 4) : ''

    const newValue = { day: paddedDay, month: paddedMonth, year: paddedYear }
    setInternalValue(newValue)
    onChange(newValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const isNumber = /\d/.test(e.key)

    if (e.key === 'ArrowRight') {
      setFocusedPart(focusedPart === 'day' ? 'month' : 'year')
      return
    }

    if (e.key === 'ArrowLeft') {
      setFocusedPart(focusedPart === 'year' ? 'month' : 'day')
      return
    }

    if (e.key === 'Backspace') {
      const updated = { ...internalValue, [focusedPart]: '' }
      setInternalValue(updated)
      onChange(updated)
      return
    }

    if (!isNumber) {
      return
    }

    const current = internalValue[focusedPart] || ''
    const maxLen = focusedPart === 'year' ? 4 : 2

    const updatedPart =
      current.length >= maxLen
        ? e.key
        : (current + e.key).slice(0, maxLen)

    const updatedValue = { ...internalValue, [focusedPart]: updatedPart }
    setInternalValue(updatedValue)
    onChange(updatedValue)

    if (focusedPart === 'day' || focusedPart === 'month') {
      if (focusTimeoutRef.current) {
        window.clearTimeout(focusTimeoutRef.current)
        focusTimeoutRef.current = null
      }

      if (updatedPart.length === maxLen) {
        setFocusedPart(focusedPart === 'day' ? 'month' : 'year')
      } else {
        focusTimeoutRef.current = window.setTimeout(() => {
          setFocusedPart(focusedPart === 'day' ? 'month' : 'year')
          focusTimeoutRef.current = null
        }, 300)
      }
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        className={className}
        value={displayValue}
        onFocus={handleFocus}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        readOnly
        style={{ caretColor: 'transparent' }}
      />
      <div
        style={{
          position: 'absolute',
          visibility: 'hidden',
          top: 0,
          left: 0,
          font: 'inherit',
          pointerEvents: 'none',
        }}
      >
        <span ref={dayRef}>{internalValue.day || 'dd'}</span>
        <span>.</span>
        <span ref={monthRef}>{internalValue.month || 'mm'}</span>
        <span>.</span>
        <span ref={yearRef}>{internalValue.year || 'yyyy'}</span>
      </div>
    </div>
  )
}

export default DottedDateInput
